import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

interface Shortcut {
  id: string
  key: string
  description: string
  category: string
  enabled: boolean
}

interface KeyboardShortcutsContextType {
  shortcuts: Shortcut[]
  isHelpOpen: boolean
  openHelp: () => void
  closeHelp: () => void
  toggleHelp: () => void
  registerCustomShortcut: (id: string, handler: () => void) => void
  unregisterCustomShortcut: (id: string) => void
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextType | undefined>(undefined)

const API_BASE = '/api/accessibility'

export const KeyboardShortcutsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [shortcuts, setShortcuts] = useState<Shortcut[]>([])
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [customHandlers, setCustomHandlers] = useState<Record<string, () => void>>({})
  const navigate = useNavigate()

  // Load shortcuts from API
  useEffect(() => {
    const loadShortcuts = async () => {
      try {
        const response = await fetch(`${API_BASE}/shortcuts`)
        if (response.ok) {
          const data = await response.json()
          setShortcuts(data)
        }
      } catch (err) {
        console.error('Failed to load shortcuts:', err)
      }
    }
    loadShortcuts()
  }, [])

  // Navigation handlers
  const navigationHandlers: Record<string, () => void> = {
    'nav.dashboard': () => navigate('/'),
    'nav.markets': () => navigate('/markets'),
    'nav.reports': () => navigate('/reports'),
    'nav.analytics': () => navigate('/analytics/categories'),
    'nav.compliance': () => navigate('/compliance'),
    'nav.query': () => navigate('/query'),
    'nav.settings': () => navigate('/settings'),
    'nav.notifications': () => navigate('/notifications'),
  }

  // Action handlers
  const actionHandlers: Record<string, () => void> = {
    'action.search': () => {
      const searchInput = document.querySelector<HTMLInputElement>('[data-search-input]')
      searchInput?.focus()
    },
    'action.help': () => setIsHelpOpen(true),
  }

  // Parse key combination
  const parseKey = (keyCombo: string): { key: string; ctrl: boolean; shift: boolean; alt: boolean } => {
    const parts = keyCombo.toLowerCase().split('+')
    return {
      key: parts[parts.length - 1],
      ctrl: parts.includes('ctrl') || parts.includes('cmd'),
      shift: parts.includes('shift'),
      alt: parts.includes('alt'),
    }
  }

  // Key sequence tracking for multi-key shortcuts like "g+d"
  const [keySequence, setKeySequence] = useState<string[]>([])

  useEffect(() => {
    let sequenceTimeout: ReturnType<typeof setTimeout>

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        // Allow escape to close modals
        if (event.key === 'Escape') {
          setIsHelpOpen(false)
        }
        return
      }

      // Handle escape
      if (event.key === 'Escape') {
        setIsHelpOpen(false)
        setKeySequence([])
        return
      }

      // Handle ? for help
      if (event.key === '?' && !event.ctrlKey && !event.altKey) {
        event.preventDefault()
        setIsHelpOpen(true)
        return
      }

      // Handle / for search
      if (event.key === '/' && !event.ctrlKey && !event.altKey) {
        event.preventDefault()
        actionHandlers['action.search']?.()
        return
      }

      // Build key sequence
      const currentKey = event.key.toLowerCase()
      const newSequence = [...keySequence, currentKey]
      setKeySequence(newSequence)

      // Clear sequence after timeout
      clearTimeout(sequenceTimeout)
      sequenceTimeout = setTimeout(() => setKeySequence([]), 1000)

      // Check for matching shortcuts
      for (const shortcut of shortcuts) {
        if (!shortcut.enabled) continue

        const parsed = parseKey(shortcut.key)

        // Check multi-key sequences (like "g+d")
        if (shortcut.key.includes('+') && !parsed.ctrl && !parsed.shift && !parsed.alt) {
          const sequenceKeys = shortcut.key.toLowerCase().split('+')
          if (
            newSequence.length >= sequenceKeys.length &&
            sequenceKeys.every((k, i) => newSequence[newSequence.length - sequenceKeys.length + i] === k)
          ) {
            event.preventDefault()
            const handler =
              navigationHandlers[shortcut.id] ||
              actionHandlers[shortcut.id] ||
              customHandlers[shortcut.id]
            handler?.()
            setKeySequence([])
            return
          }
        }

        // Check modifier shortcuts
        if (
          parsed.ctrl === event.ctrlKey &&
          parsed.shift === event.shiftKey &&
          parsed.alt === event.altKey &&
          parsed.key === currentKey
        ) {
          event.preventDefault()
          const handler =
            navigationHandlers[shortcut.id] ||
            actionHandlers[shortcut.id] ||
            customHandlers[shortcut.id]
          handler?.()
          setKeySequence([])
          return
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      clearTimeout(sequenceTimeout)
    }
  }, [shortcuts, keySequence, customHandlers, navigate])

  const openHelp = useCallback(() => setIsHelpOpen(true), [])
  const closeHelp = useCallback(() => setIsHelpOpen(false), [])
  const toggleHelp = useCallback(() => setIsHelpOpen((prev) => !prev), [])

  const registerCustomShortcut = useCallback((id: string, handler: () => void) => {
    setCustomHandlers((prev) => ({ ...prev, [id]: handler }))
  }, [])

  const unregisterCustomShortcut = useCallback((id: string) => {
    setCustomHandlers((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [])

  return (
    <KeyboardShortcutsContext.Provider
      value={{
        shortcuts,
        isHelpOpen,
        openHelp,
        closeHelp,
        toggleHelp,
        registerCustomShortcut,
        unregisterCustomShortcut,
      }}
    >
      {children}
      {isHelpOpen && <KeyboardShortcutsModal onClose={closeHelp} shortcuts={shortcuts} />}
    </KeyboardShortcutsContext.Provider>
  )
}

// Keyboard shortcuts modal component
const KeyboardShortcutsModal: React.FC<{ onClose: () => void; shortcuts: Shortcut[] }> = ({
  onClose,
  shortcuts,
}) => {
  const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = []
    }
    acc[shortcut.category].push(shortcut)
    return acc
  }, {} as Record<string, Shortcut[]>)

  const categoryNames: Record<string, string> = {
    navigation: 'Navigation',
    actions: 'Actions',
    interface: 'Interface',
    data: 'Data Navigation',
  }

  const formatKey = (key: string) => {
    return key
      .split('+')
      .map((k) => {
        if (k === 'ctrl') return 'Ctrl'
        if (k === 'shift') return 'Shift'
        if (k === 'alt') return 'Alt'
        if (k === 'cmd') return 'Cmd'
        if (k === 'esc') return 'Esc'
        if (k === 'enter') return 'Enter'
        if (k === 'del') return 'Delete'
        if (k === 'tab') return 'Tab'
        return k.toUpperCase()
      })
      .join(' + ')
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortcuts-title"
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '700px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 id="shortcuts-title" style={{ margin: 0, color: '#1F2937', fontSize: '20px' }}>
            Keyboard Shortcuts
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6B7280',
              padding: '4px',
            }}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div style={{ display: 'grid', gap: '24px' }}>
          {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
            <div key={category}>
              <h3 style={{ margin: '0 0 12px 0', color: '#374151', fontSize: '16px', fontWeight: 600 }}>
                {categoryNames[category] || category}
              </h3>
              <div style={{ display: 'grid', gap: '8px' }}>
                {categoryShortcuts
                  .filter((s) => s.enabled)
                  .map((shortcut) => (
                    <div
                      key={shortcut.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '6px',
                      }}
                    >
                      <span style={{ color: '#4B5563', fontSize: '14px' }}>{shortcut.description}</span>
                      <kbd
                        style={{
                          padding: '4px 8px',
                          backgroundColor: 'white',
                          border: '1px solid #D1D5DB',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontFamily: 'monospace',
                          color: '#1F2937',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        }}
                      >
                        {formatKey(shortcut.key)}
                      </kbd>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: '20px',
            padding: '12px',
            backgroundColor: '#F3F4F6',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#6B7280',
          }}
        >
          Press <kbd style={{ padding: '2px 6px', backgroundColor: 'white', borderRadius: '3px', border: '1px solid #D1D5DB' }}>?</kbd> at any time to show this help dialog.
          Press <kbd style={{ padding: '2px 6px', backgroundColor: 'white', borderRadius: '3px', border: '1px solid #D1D5DB' }}>Esc</kbd> to close.
        </div>
      </div>
    </div>
  )
}

export const useKeyboardShortcuts = (): KeyboardShortcutsContextType => {
  const context = useContext(KeyboardShortcutsContext)
  if (!context) {
    throw new Error('useKeyboardShortcuts must be used within a KeyboardShortcutsProvider')
  }
  return context
}
